import { Box, Paper, Stack, Typography } from "@mui/material";
import type { ReactNode } from "react";
import { useAppCustomStyles } from "../../assets/theme/custom/hooks";
import { pageSectionCardFillStyles } from "../../assets/theme/custom/layout/pageLayout";

type Props = {
  title?: string;
  description?: string;
  actions?: ReactNode;
  children: ReactNode;
  noPadding?: boolean;
  fillHeight?: boolean;
};

/**
 * Section card with header band — used in widget studio, FormSection, and nested editors.
 * Matches PageSectionPanel styling with optional titled header.
 */
export default function PageSectionCard({
  title,
  description,
  actions,
  children,
  noPadding,
  fillHeight,
}: Props) {
  const custom = useAppCustomStyles();
  const fillSx = pageSectionCardFillStyles(Boolean(fillHeight));

  return (
    <Paper
      elevation={0}
      sx={[
        custom.page.pageSectionCard,
        fillSx,
        fillHeight ? { display: "flex", flexDirection: "column" } : null,
      ]}
    >
      {(title || description || actions) && (
        <Stack
          direction="row"
          alignItems="flex-start"
          justifyContent="space-between"
          spacing={1}
          sx={custom.page.pageSectionCardHeader}
        >
          <Box sx={custom.page.pageSectionCardTitleBox}>
            {title ? (
              <Typography variant="subtitle2" fontWeight={700}>
                {title}
              </Typography>
            ) : null}
            {description ? (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={title ? custom.page.pageSectionCardTitleCaptionWithTitle : custom.page.pageSectionCardTitleCaption}
              >
                {description}
              </Typography>
            ) : null}
          </Box>
          {actions}
        </Stack>
      )}
      <Box
        sx={[
          noPadding ? custom.page.pageSectionCardBodyNoPadding : custom.page.pageSectionCardBody,
          fillSx,
        ]}
      >
        {children}
      </Box>
      <Box aria-hidden sx={custom.page.pageSectionCardFooter} />
    </Paper>
  );
}
